import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../reducers/store";
import { UserProgress } from "../model/progress";
import { AlertView } from "./alert";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, TableSortLabel } from "@mui/material";
import { UserProgressCourseRow } from "./userprogresscourse";
import { User } from "../model/user";
import { Course } from "../model/course";
import { sortCurrentUsersProgramProgress } from "../reducers/progressSlice";
import { sortCurrentGroupUsers } from "../reducers/userSlice";
import { Program } from "../model/program";

export const UserProgressView = () => {
    const dispatch: AppDispatch = useDispatch();
    const [errorMessage, setError] = useState<string | null>(null);

    const [emailAscending, setEmailAscending] = useState(true);
    const [firstNameAscending, setFirstNameAscending] = useState(true);
    const [lastNameAscending, setLastNameAscending] = useState(true);
    const [departmentAscending, setDepartmentAscending] = useState(true);

    const currentProgram: Program | null = useSelector((state: RootState) => state.programs.currentProgram);
    const allCourses: Course[] = useSelector((state: RootState) => state.courses.courses);
    const currentCourses = allCourses.filter((course) => course.programId == currentProgram?.id)

    const currentGroupUsers: User[] = useSelector((state: RootState) => state.users.currentGroupUsers);
    const currentUsersProgramProgress: UserProgress[] | null = useSelector((state: RootState) => state.progress.currentUsersProgramProgress);
    
    let startedUserIds: string[] | undefined = currentUsersProgramProgress?.map(progress => progress.userId);
    let noDataUsers = currentGroupUsers.filter(user => !startedUserIds?.includes(user.id));

    const findUser = (userId: string): User | undefined => currentGroupUsers.find(u => u.id === userId);

    useEffect(() => {
        setLastNameAscending(false)
        dispatch(sortCurrentUsersProgramProgress({ascending: true, field: "lastName"}));
        dispatch(sortCurrentGroupUsers({ascending: true, field: "lastName"}));
    }, []);

    return (
        <div style={{padding: 16}}>
            <AlertView error={errorMessage}/>
            <Typography sx={{ marginBottom: 2 }} align='left'>In Progress</Typography>

            <TableContainer component={Paper} sx={{ marginBottom: 5 }} >
                <Table aria-label="collapsible table">
                    <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell>
                            <Tooltip title="Progress is measured by the amount of chapters the user has completed">
                                <span style={{ cursor: 'help' }}>
                                    Progress
                                </span>
                            </Tooltip>
                        </TableCell>
                        <TableCell width={350}>
                            <TableSortLabel
                                active={true}
                                direction={emailAscending ? 'asc' : 'desc'}
                                onClick={() => {
                                    setEmailAscending(!emailAscending)
                                    dispatch(sortCurrentUsersProgramProgress({ascending: emailAscending, field: "email"}))
                                    dispatch(sortCurrentGroupUsers({ascending: emailAscending, field: "email"}))
                                }}
                            >
                                Email
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={true}
                                direction={firstNameAscending ? 'asc' : 'desc'}
                                onClick={() => {
                                    setFirstNameAscending(!firstNameAscending)
                                    dispatch(sortCurrentUsersProgramProgress({ascending: firstNameAscending, field: "firstName"}))
                                    dispatch(sortCurrentGroupUsers({ascending: firstNameAscending, field: "firstName"}))
                                }}
                            >
                                First Name
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={true}
                                direction={lastNameAscending ? 'asc' : 'desc'}
                                onClick={() => {
                                    setLastNameAscending(!lastNameAscending)
                                    dispatch(sortCurrentUsersProgramProgress({ascending: lastNameAscending, field: "lastName"}))
                                    dispatch(sortCurrentGroupUsers({ascending: lastNameAscending, field: "lastName"}))
                                }}
                            >
                                Last Name
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={true}
                                direction={departmentAscending ? 'asc' : 'desc'}
                                onClick={() => {
                                    setDepartmentAscending(!departmentAscending)
                                    dispatch(sortCurrentUsersProgramProgress({ascending: departmentAscending, field: "department"}))
                                    dispatch(sortCurrentGroupUsers({ascending: departmentAscending, field: "department"}))
                                }}
                            >
                                Department
                            </TableSortLabel>
                        </TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                    {
                        currentUsersProgramProgress?.map((userProgress) => (
                            userProgress.userId ? 
                                <UserProgressCourseRow
                                    key={userProgress.userId} 
                                    userProgress={userProgress} 
                                    currentProgram={currentProgram} 
                                    currentCourses={currentCourses}
                                    firstName={findUser(userProgress.userId)?.firstName ?? ""}
                                    lastName={findUser(userProgress.userId)?.lastName ?? ""}
                                    department={findUser(userProgress.userId)?.department ?? ""}
                                />:
                                <></>
                        ))
                    }
                    </TableBody>
                </Table>
            </TableContainer>

            {
                currentProgram?.clientName != "Public" ? (
                    <>
                        <Typography sx={{ marginBottom: 2 }} align='left'>No Progress</Typography>
                        <TableContainer component={Paper}>
                            <Table aria-label="regular table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell/>
                                        <TableCell width={350}>
                                            <TableSortLabel
                                                active={true}
                                                direction={emailAscending ? 'asc' : 'desc'}
                                                onClick={() => {
                                                    setEmailAscending(!emailAscending)
                                                    dispatch(sortCurrentUsersProgramProgress({ascending: emailAscending, field: "email"}))
                                                    dispatch(sortCurrentGroupUsers({ascending: emailAscending, field: "email"}))
                                                }}
                                            >
                                                Email
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={true}
                                                direction={firstNameAscending ? 'asc' : 'desc'}
                                                onClick={() => {
                                                    setFirstNameAscending(!firstNameAscending)
                                                    dispatch(sortCurrentUsersProgramProgress({ascending: firstNameAscending, field: "firstName"}))
                                                    dispatch(sortCurrentGroupUsers({ascending: firstNameAscending, field: "firstName"}))
                                                }}
                                            >
                                                First Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={true}
                                                direction={lastNameAscending ? 'asc' : 'desc'}
                                                onClick={() => {
                                                    setLastNameAscending(!lastNameAscending)
                                                    dispatch(sortCurrentUsersProgramProgress({ascending: lastNameAscending, field: "lastName"}))
                                                    dispatch(sortCurrentGroupUsers({ascending: lastNameAscending, field: "lastName"}))
                                                }}
                                            >
                                                Last Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={true}
                                                direction={departmentAscending ? 'asc' : 'desc'}
                                                onClick={() => {
                                                    setDepartmentAscending(!departmentAscending)
                                                    dispatch(sortCurrentUsersProgramProgress({ascending: departmentAscending, field: "department"}))
                                                    dispatch(sortCurrentGroupUsers({ascending: departmentAscending, field: "department"}))
                                                }}
                                            >
                                                Department
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        noDataUsers.map((user) => (
                                            <TableRow key={user.id}>
                                                <TableCell width={365}></TableCell>
                                                <TableCell width={350}>{user.email}</TableCell>
                                                <TableCell>{user.firstName}</TableCell>
                                                <TableCell>{user.lastName}</TableCell>
                                                <TableCell>{user.department}</TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                ): <></>
            }
        </div>
    )
}